import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import {
  EmployeeEditLeavePageDocument,
  TimeoffEmployeeRootPageDocument,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { SkeletonModal } from 'modules/core/components/Modal'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'

import EditLeaveModal from 'modules/timeoff/components/EditLeaveModal'
import LeaveRecommendationModal from 'modules/timeoff/components/LeaveRecommendationModal'
import { useLeaveRecommendationFeatureFlag } from 'modules/timeoff/hooks/useLeaveRecommendationFeatureFlag'

import { useOnClose } from './useOnClose'

const EditLeavePage = () => {
  const { leave_id: leaveId, id } = useStrictParams<'id' | 'leave_id'>()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const apolloClient = useApolloClient()

  const onReject = () => {
    navigate(
      navigation.employees.id.timeOff.leavesRejectLeaveId.fullPath({ id, leave_id: leaveId })
    )
  }

  const onClose = useOnClose(id)

  const onCloseRecommendationModal = () => {
    apolloClient.refetchQueries({
      include: [TimeoffEmployeeRootPageDocument],
    })
    onClose()
  }

  const { loading: ffLoading, enabled: isRecommendationEnabled } =
    useLeaveRecommendationFeatureFlag()

  const { data, loading } = useQuery(EmployeeEditLeavePageDocument, {
    variables: {
      leaveId: String(leaveId),
    },
  })

  if (loading || ffLoading) {
    return <SkeletonModal />
  }

  if (!data?.apiCore.currentsConnection.edges[0]?.node.employee) {
    throw new Error('Current employee not found')
  }

  const currentEmployee = data.apiCore.currentsConnection.edges[0].node.employee

  const customResourceFieldsData =
    compact(
      data?.documents.foldersConnection.edges[0]?.node?.resourceFieldsConnection.edges.map(
        (edge) => edge.node
      )
    ) ?? []

  const leave = data?.timeoff.leave

  if (!leave) {
    throw new Error('Leave not found')
  }

  const leaveEmployee = leave.employee

  if (!leaveEmployee) {
    throw new Error('Leave employee not found')
  }

  const sickFolderId = data.documents.foldersConnection.edges[0]?.node?.id

  if (!sickFolderId) {
    throw new Error('Sick folder not found')
  }

  return (
    <>
      {isRecommendationEnabled ? (
        <LeaveRecommendationModal
          currentEmployee={currentEmployee}
          customResourceFieldsData={customResourceFieldsData}
          delegatedApprover={leave.delegatedApproverAccess}
          leaveEmployee={leaveEmployee}
          leaveId={String(leaveId)}
          onChange={onCloseRecommendationModal}
          onClose={onCloseRecommendationModal}
          onReject={onReject}
          sickFolderId={sickFolderId}
          leaveCanManageLeaveDocuments={leave._policies?.manageLeaveDocuments?.can ?? false}
          recommendation={leave.approvalRecommendation}
        />
      ) : (
        <EditLeaveModal
          currentEmployee={currentEmployee}
          customResourceFieldsData={customResourceFieldsData}
          delegatedApprover={leave.delegatedApproverAccess}
          leaveEmployee={leaveEmployee}
          leaveId={String(leaveId)}
          onChange={onClose}
          onClose={onClose}
          onReject={onReject}
          sickFolderId={sickFolderId}
          leaveCanManageLeaveDocuments={leave._policies?.manageLeaveDocuments?.can ?? false}
        />
      )}
    </>
  )
}

export default EditLeavePage
